/**
 * Síntesis de los SFX con Web Audio. Cada `SoundSpec` se renderiza a un
 * `AudioBuffer` con `OfflineAudioContext` (offline = tan rápido como el CPU
 * lo permita, no en tiempo real) y se normaliza a pico 0.9. El motor cachea el
 * resultado, así el costo de síntesis se paga una sola vez por sonido.
 *
 * Voces:
 *  - `mallet` (marimba): fundamental + armónico ×4 con ataque rápido, lowpass.
 *  - `bell` (campanita de vidrio): partiales inarmónicos 1 / 2.76 / 5.4.
 *  - `pad` (swell): sines detuneados, ataque lento, lowpass — para sesión.
 *  - `noise`: ruido blanco por un pasabanda que barre (encaje y despegue de la nave).
 * Reverb común: `ConvolverNode` con un impulso sintético corto y cálido.
 */
import type { SoundSpec, SfxNote, SfxBell, SfxPad, SfxNoise } from './sounds.js';

const SAMPLE_RATE = 44100;
const REVERB_SECONDS = 0.45;
const REVERB_WET = 0.16;
const PEAK_TARGET = 0.9;

/** Impulso de reverb: ruido con decay cúbico. Cálido y corto. */
function impulse(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / len;
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 3.2);
  }
  return buf;
}

function mallet(ctx: BaseAudioContext, out: AudioNode, note: SfxNote): void {
  const { t, f, dur } = note;
  const g = note.g;
  const bright = note.bright ?? 1;
  const body = ctx.createOscillator();
  body.type = 'sine';
  body.frequency.value = f;
  const har = ctx.createOscillator();
  har.type = 'sine';
  har.frequency.value = f * 4;
  const gb = ctx.createGain();
  const gh = ctx.createGain();
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 3000;
  gb.gain.setValueAtTime(0.0001, t);
  gb.gain.linearRampToValueAtTime(g, t + 0.004);
  gb.gain.exponentialRampToValueAtTime(0.0006, t + dur);
  gh.gain.setValueAtTime(0.0001, t);
  gh.gain.linearRampToValueAtTime(g * 0.3 * bright, t + 0.003);
  gh.gain.exponentialRampToValueAtTime(0.0004, t + 0.09);
  body.connect(gb).connect(lp);
  har.connect(gh).connect(lp);
  lp.connect(out);
  body.start(t);
  har.start(t);
  body.stop(t + dur + 0.05);
  har.stop(t + 0.12);
}

function bell(ctx: BaseAudioContext, out: AudioNode, b: SfxBell): void {
  const partials = [1, 2.76, 5.4];
  const decays = [b.dur, b.dur * 0.7, b.dur * 0.5];
  partials.forEach((p, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = b.f * p;
    const gg = ctx.createGain();
    gg.gain.setValueAtTime(0.0001, b.t);
    gg.gain.linearRampToValueAtTime(b.g / (i + 1), b.t + 0.006);
    gg.gain.exponentialRampToValueAtTime(0.0003, b.t + (decays[i] ?? b.dur));
    osc.connect(gg).connect(out);
    osc.start(b.t);
    osc.stop(b.t + (decays[i] ?? b.dur) + 0.05);
  });
}

function pad(ctx: BaseAudioContext, out: AudioNode, p: SfxPad): void {
  [1, 1.005].forEach((det) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = p.f * det;
    const gg = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400;
    gg.gain.setValueAtTime(0.0001, p.t);
    gg.gain.linearRampToValueAtTime(p.g, p.t + 0.15);
    gg.gain.setValueAtTime(p.g, p.t + p.dur * 0.5);
    gg.gain.exponentialRampToValueAtTime(0.0004, p.t + p.dur);
    osc.connect(gg).connect(lp).connect(out);
    osc.start(p.t);
    osc.stop(p.t + p.dur + 0.05);
  });
}

function noise(ctx: BaseAudioContext, out: AudioNode, r: SfxNoise): void {
  const len = Math.ceil((r.dur + 0.05) * ctx.sampleRate);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 0.9;
  bp.frequency.setValueAtTime(r.f0, r.t);
  bp.frequency.exponentialRampToValueAtTime(r.f1, r.t + r.dur);
  const gg = ctx.createGain();
  gg.gain.setValueAtTime(0.0001, r.t);
  // Los largos (rumor) crecen hasta la mitad; los cortos (golpe) atacan en ~20 ms.
  gg.gain.linearRampToValueAtTime(
    r.g,
    r.t + Math.min(0.02, r.dur * 0.2) + (r.dur > 0.5 ? r.dur * 0.5 : 0),
  );
  gg.gain.exponentialRampToValueAtTime(0.0004, r.t + r.dur);
  src.connect(bp).connect(gg).connect(out);
  src.start(r.t);
  src.stop(r.t + r.dur + 0.05);
}

/** ¿Está disponible `OfflineAudioContext` en este entorno? (guarda SSR/tests). */
export function canRender(): boolean {
  return typeof OfflineAudioContext !== 'undefined';
}

/**
 * Renderiza una spec a un `AudioBuffer` mono normalizado a pico 0.9.
 * Rechaza si `OfflineAudioContext` no existe (SSR/tests).
 */
export async function renderSpec(spec: SoundSpec): Promise<AudioBuffer> {
  if (!canRender()) throw new Error('OfflineAudioContext no disponible');
  const length = Math.ceil(spec.len * SAMPLE_RATE) + Math.floor(SAMPLE_RATE * 0.15);
  const off = new OfflineAudioContext(1, length, SAMPLE_RATE);
  const master = off.createGain();
  master.gain.value = 1;
  const wet = off.createGain();
  wet.gain.value = REVERB_WET;
  const conv = off.createConvolver();
  conv.buffer = impulse(off, REVERB_SECONDS);
  master.connect(off.destination);
  master.connect(conv).connect(wet).connect(off.destination);
  for (const p of spec.pads ?? []) pad(off, master, p);
  for (const n of spec.notes ?? []) mallet(off, master, n);
  for (const b of spec.bells ?? []) bell(off, master, b);
  for (const r of spec.noises ?? []) noise(off, master, r);
  const buf = await off.startRendering();
  // Peak-normalizar in place a 0.9 (loudness parejo entre sonidos).
  const data = buf.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i] ?? 0);
    if (a > peak) peak = a;
  }
  if (peak > 0) {
    const k = PEAK_TARGET / peak;
    for (let i = 0; i < data.length; i++) data[i] = (data[i] ?? 0) * k;
  }
  return buf;
}
